import { type AppPlatform, convertToInr, LABELS, toDecimal } from '@mm/shared';
import clsx from 'clsx';
import { Trash2, Undo2 } from 'lucide-react';
import { type KeyboardEvent, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AppIcon } from '../../components/AppIcon';
import { LiveRatesStrip } from '../../components/LiveRates';
import { Badge, Card, EmptyState } from '../../components/ui/display';
import { formatMoney } from '../../utils/format';

// ── API shapes ───────────────────────────────────────────────────────

export interface RevenueEntryRef {
  id: string;
  revenueAmount: string;
  exchangeRate: string;
  exchangeRateSource: 'MONTHLY' | 'MANUAL';
  revenueInr: string;
  updatedAt: string;
}

export interface MorningApp {
  id: string;
  name: string;
  iconUrl: string | null;
  developerName: string | null;
  platform: AppPlatform;
  status: string;
}

export interface RevenueServerRow {
  /** `${appId}|${accountId}` — one row per app + account assignment. */
  key: string;
  app: MorningApp;
  account: {
    id: string;
    accountName: string;
    accountIdentifier: string | null;
    status: string;
    currencyCode: string;
    revenueSource: { id: string; name: string; code: string };
  };
  /** The account was assigned to the app on this date. */
  assigned: boolean;
  active: boolean;
  expected: boolean;
  monthlyRate: string | null;
  entry: RevenueEntryRef | null;
  previous: RevenueEntryRef | null;
}

// ── Row state ────────────────────────────────────────────────────────

export interface RevenueRowState {
  key: string;
  server: RevenueServerRow;
  /** '' means "not entered" — never saved as 0. */
  amount: string;
  /** Exchange rate used; prefilled from the month's rate, editable (manual override). */
  rate: string;
  hidden: boolean;
  remove: boolean;
}

const AMOUNT_RE = /^\d{1,14}(\.\d{1,4})?$/;
const RATE_RE = /^\d{1,12}(\.\d{1,6})?$/;

const fmtRate = (v: string | null | undefined) => (v == null ? '' : String(Number(v)));

export function initialRevenueRows(rows: RevenueServerRow[]): RevenueRowState[] {
  return rows.map((r) => ({
    key: r.key,
    server: r,
    amount: r.entry ? String(Number(r.entry.revenueAmount)) : '',
    rate: fmtRate(r.entry?.exchangeRate ?? r.monthlyRate),
    hidden: !r.active && !r.entry,
    remove: false,
  }));
}

export type RevenueRowStatus =
  | 'saved'
  | 'changed'
  | 'new'
  | 'missing'
  | 'remove'
  | 'invalid'
  | 'needsRate'
  | 'optional';

const rateValid = (r: RevenueRowState) => RATE_RE.test(r.rate) && Number(r.rate) > 0;

export function revenueRowStatus(r: RevenueRowState): RevenueRowStatus {
  const e = r.server.entry;
  if (r.remove) return 'remove';
  if (r.amount !== '' && !AMOUNT_RE.test(r.amount)) return 'invalid';
  if (r.amount !== '' && !rateValid(r)) return 'needsRate';
  if (e) {
    if (r.amount === '') return 'invalid';
    const same =
      toDecimal(r.amount).equals(e.revenueAmount) && toDecimal(r.rate).equals(e.exchangeRate);
    return same ? 'saved' : 'changed';
  }
  if (r.amount !== '') return 'new';
  return r.server.expected ? 'missing' : 'optional';
}

/** Rate differs from the month's configured rate (or none is configured) → saved as MANUAL. */
export function isManualRate(r: RevenueRowState): boolean {
  if (r.server.account.currencyCode === 'INR') return false;
  if (!r.server.monthlyRate) return true;
  return r.rate !== '' && rateValid(r) && !toDecimal(r.rate).equals(r.server.monthlyRate);
}

/** Exchange rate to send: only when it is not what the server would use anyway. */
export function rateToSend(r: RevenueRowState): string | undefined {
  if (r.server.account.currencyCode === 'INR') return undefined;
  const baseline = r.server.entry?.exchangeRate ?? r.server.monthlyRate;
  if (baseline && toDecimal(r.rate).equals(baseline)) return undefined;
  return r.rate;
}

export function revenuePreviewInr(r: RevenueRowState) {
  if (r.remove || r.amount === '' || !AMOUNT_RE.test(r.amount) || !rateValid(r)) return null;
  return convertToInr(r.amount, r.rate);
}

const STATUS_UI: Record<
  RevenueRowStatus,
  { label: string; tone: 'green' | 'amber' | 'blue' | 'slate' | 'rose' }
> = {
  saved: { label: 'Saved', tone: 'green' },
  changed: { label: 'Changed', tone: 'amber' },
  new: { label: 'New', tone: 'blue' },
  missing: { label: 'Missing', tone: 'rose' },
  optional: { label: 'Not expected', tone: 'slate' },
  remove: { label: 'Will be deleted', tone: 'rose' },
  invalid: { label: 'Check amount', tone: 'rose' },
  needsRate: { label: 'Rate needed', tone: 'rose' },
};

/** App logo, name, developer account and platform (morning entry rows). */
export function AppCell({ app }: { app: MorningApp }) {
  return (
    <span className="flex items-center gap-2.5">
      <AppIcon src={app.iconUrl} name={app.name} size={32} />
      <span className="min-w-0">
        <span className="block font-medium text-slate-900">{app.name}</span>
        <span className="block truncate text-xs text-slate-400">
          {[app.developerName, LABELS.appPlatform[app.platform]].filter(Boolean).join(' · ')}
        </span>
      </span>
    </span>
  );
}

/** Revenue section of morning entry: App | Source | Account | Currency | Revenue | Rate | INR. */
export function RevenueSection({
  rows,
  statuses,
  showPrevious,
  canOverrideRate,
  onChange,
  actions,
}: {
  rows: RevenueRowState[];
  statuses: Map<string, RevenueRowStatus>;
  showPrevious: boolean;
  /** Only administrators may change the month's exchange rate on an entry. */
  canOverrideRate: boolean;
  onChange: (key: string, patch: Partial<RevenueRowState>) => void;
  actions?: React.ReactNode;
}) {
  const inputs = useRef<Map<string, HTMLInputElement>>(new Map());
  const visible = rows.filter((r) => !r.hidden);
  const missingRates = [
    ...new Set(
      visible
        .filter((r) => !r.server.monthlyRate && r.server.account.currencyCode !== 'INR')
        .map((r) => r.server.account.currencyCode),
    ),
  ];

  const focusNext = (key: string, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const order = visible.map((r) => r.key);
    const next = order[order.indexOf(key) + (e.key === 'ArrowUp' ? -1 : 1)];
    if (next) inputs.current.get(next)?.select();
  };

  return (
    <Card
      title="2. Revenue"
      description={
        canOverrideRate
          ? "Enter revenue in each account's currency. The month's exchange rate is applied and saved with the entry; change it only if a different rate applies."
          : "Enter revenue in each account's currency. The month's exchange rate is applied and saved with the entry."
      }
      bodyClassName="p-0"
      actions={actions}
    >
      <LiveRatesStrip className="border-b border-slate-100 bg-slate-50/60 px-4 py-2" />
      {missingRates.length > 0 && (
        <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          No {missingRates.join(', ')} rate is configured for this month.{' '}
          <Link to="/currency-rates" className="font-medium underline">
            Add it under Currency Rates
          </Link>{' '}
          {canOverrideRate ? 'or type the rate on each row.' : 'before saving these rows.'}
        </div>
      )}
      {visible.length === 0 ? (
        <EmptyState
          title="No revenue accounts assigned"
          description="Assign AdMob, AdX or Google Play accounts to your apps (on each app's page) to enter revenue here."
        />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">App</th>
                <th className="px-4 py-3 text-left">Source</th>
                <th className="px-4 py-3 text-left">Account</th>
                <th className="px-4 py-3 text-left">Currency</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3 text-right">Rate (₹)</th>
                <th className="px-4 py-3 text-right">INR</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r) => {
                const st = statuses.get(r.key)!;
                const a = r.server.account;
                const inr = revenuePreviewInr(r);
                const inrAccount = a.currencyCode === 'INR';
                const manual = isManualRate(r);
                return (
                  <tr
                    key={r.key}
                    className={clsx(
                      r.remove && 'bg-rose-50/60 text-slate-400 line-through',
                      st === 'missing' && 'bg-amber-50/30',
                    )}
                  >
                    <td className="px-4 py-2">
                      <AppCell app={r.server.app} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{a.revenueSource.name}</td>
                    <td className="px-4 py-2">
                      <div className="text-slate-900">{a.accountName}</div>
                      {a.accountIdentifier && (
                        <div className="text-xs text-slate-400">{a.accountIdentifier}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{a.currencyCode}</td>
                    <td className="px-4 py-2 text-right">
                      <input
                        ref={(el) => {
                          if (el) inputs.current.set(r.key, el);
                          else inputs.current.delete(r.key);
                        }}
                        aria-label={`Revenue for ${r.server.app.name} ${a.accountName}`}
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={r.remove}
                        value={r.amount}
                        placeholder="—"
                        onChange={(e) =>
                          onChange(r.key, { amount: e.target.value.replace(/,/g, '').trim() })
                        }
                        onKeyDown={(e) => focusNext(r.key, e)}
                        onFocus={(e) => e.target.select()}
                        className={clsx(
                          'num h-9 w-32 rounded-md border px-2 text-right focus:ring-2 focus:outline-none',
                          st === 'invalid'
                            ? 'border-rose-400 focus:ring-rose-200'
                            : 'border-slate-300 focus:border-brand-500 focus:ring-brand-500/20',
                        )}
                      />
                      {showPrevious && (
                        <div className="num mt-0.5 text-xs text-slate-400">
                          Prev:{' '}
                          {r.server.previous
                            ? formatMoney(r.server.previous.revenueAmount, a.currencyCode)
                            : 'no entry'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input
                        aria-label={`Exchange rate for ${a.accountName}`}
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={r.remove || inrAccount || !canOverrideRate}
                        title={
                          canOverrideRate ? undefined : 'Only an administrator can change the rate'
                        }
                        value={inrAccount ? '1' : r.rate}
                        placeholder="Rate"
                        onChange={(e) => onChange(r.key, { rate: e.target.value.trim() })}
                        className={clsx(
                          'num h-9 w-24 rounded-md border px-2 text-right focus:ring-2 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400',
                          st === 'needsRate'
                            ? 'border-rose-400 focus:ring-rose-200'
                            : manual
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-slate-300 focus:border-brand-500 focus:ring-brand-500/20',
                        )}
                      />
                      {manual && r.rate !== '' && (
                        <div className="mt-0.5 text-[10px] text-amber-600">manual rate</div>
                      )}
                    </td>
                    <td className="num px-4 py-2 text-right font-medium text-slate-900">
                      {inr ? formatMoney(inr.toString()) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={STATUS_UI[st].tone}>{STATUS_UI[st].label}</Badge>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.server.entry ? (
                        <button
                          type="button"
                          onClick={() => onChange(r.key, { remove: !r.remove })}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label={r.remove ? 'Keep saved entry' : 'Delete saved entry'}
                          title={r.remove ? 'Keep saved entry' : 'Delete saved entry'}
                        >
                          {r.remove ? <Undo2 className="size-4" /> : <Trash2 className="size-4" />}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onChange(r.key, { hidden: true, amount: '' })}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          aria-label="Remove row"
                          title="Remove row (nothing is saved)"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
